import * as angular from 'angular';

// models
import { ServerMessage, ServerMessageResponse } from './server-message.models';

(function () {
    'use strict';

    angular.module('app').service('ServerMessageService', ServerMessageService);

    function ServerMessageService(
        $compile,
        $http,
        $rootScope,
        LocalStorageService
    ) {
        const STORE_KEY = 'common/server_messages';

        let scope, messageHtmlElement, userId;

        this.init = (): void => {
            getServerMessage();

            document.addEventListener(
                'visibilitychange',
                () => {
                    if (!document.hidden) {
                        getServerMessage();
                    }
                },
                false
            );
        };

        this.closeMessage = (message: ServerMessage): void => {
            updateStoredMessage(message, true);
            hideMessage();
        };

        function getServerMessage(): void {
            userId = LocalStorageService.getObject('common/user')?.id;

            $http
                .get('/api/v1/maintenance_console/message/')
                .then(constructServerMessage)
                .then(syncServerMessage)
                .then(checkIfMessageShouldBeDisplayed);
        }

        function loadMessagesFromStorage(): ServerMessage[] {
            return LocalStorageService.getObject(STORE_KEY) || [];
        }

        function saveMessageInStorage(message: ServerMessage): void {
            const storedMessages = loadMessagesFromStorage();
            const storedMessageIndex = storedMessages.findIndex((message) => {
                return !message.dismissible || message.userId === userId;
            });

            if (storedMessageIndex === -1) {
                storedMessages.push(message);
            } else {
                storedMessages[storedMessageIndex] = message;
            }

            LocalStorageService.saveObject(STORE_KEY, storedMessages);
        }

        function updateStoredMessage(
            newMessage: ServerMessage,
            updateAfterDismiss?: boolean
        ): void {
            const storedMessages = loadMessagesFromStorage();
            const storedMessage = findUserMessage(storedMessages);

            storedMessage.dismissible = newMessage.dismissible;
            storedMessage.displayFrequency = newMessage.displayFrequency;
            storedMessage.maxDisplayCount = newMessage.maxDisplayCount;
            storedMessage.message = newMessage.message;

            /**
             * After dismiss we want to update displayedNumber counter and
             * lastDismissDate for future calculations.
             */
            if (updateAfterDismiss) {
                storedMessage.displayedNumber = newMessage.displayedNumber;
                storedMessage.lastDismissDate = newMessage.lastDismissDate;
            }

            saveMessageInStorage(storedMessage);
        }

        function removeStoredMessage(): void {
            LocalStorageService.delete(STORE_KEY);
        }

        function constructServerMessage(response): ServerMessage {
            const message: ServerMessageResponse = response.data;

            if (!message?.message_id) {
                return;
            }

            return {
                message: message.message_body_web,
                lastDismissDate: null,
                dismissible: message.dismissible,
                displayFrequency: message.display_frequency,
                messageId: message.message_id,
                maxDisplayCount: message.max_display_count,
                displayedNumber: 0,

                /**
                 * If message is not dismissible, it should be displayed
                 * for every user.
                 */
                userId: (() => (message.dismissible ? userId : null))(),
            } as ServerMessage;
        }

        function syncServerMessage(serverMessage: ServerMessage): void {
            /**
             * If there is no serverMessage to display, remove all stored messages.
             */
            if (!serverMessage) {
                removeStoredMessage();
                return;
            }

            let storedMessages = loadMessagesFromStorage();
            let storedMessage = findUserMessage(storedMessages);

            /**
             * If message changed dismissible property,
             * remove all stored messages.
             */
            if (
                storedMessages.some((message) => {
                    return message.dismissible !== serverMessage.dismissible;
                })
            ) {
                removeStoredMessage();

                storedMessages = [];
                storedMessage = null;
            }

            /**
             * Don't store dismissible messages without userId. We cannot count, which user
             * displayed this message already.
             */
            if (serverMessage.dismissible && !serverMessage.userId) {
                return;
            }

            /**
             * If serverMessage is not stored, save it.
             */
            if (!storedMessages.length || !storedMessage) {
                saveMessageInStorage(serverMessage);
                return;
            }

            /**
             * If message is stored, update properties.
             */
            updateStoredMessage(serverMessage);
        }

        function checkIfMessageShouldBeDisplayed() {
            const storedMessages = loadMessagesFromStorage();
            const storedMessage = findUserMessage(storedMessages);

            hideMessage();

            /**
             * Ensures that there is message to consider.
             */
            if (!storedMessages.length || !storedMessage) {
                return;
            }

            /**
             * Ensures that message is displayed only a limited number of times,
             * according to the limit set in the maxDisplayCount property.
             */
            if (
                storedMessage.displayedNumber >= storedMessage.maxDisplayCount
            ) {
                return;
            }

            /**
             * Determine if message should be displayed based on the elapsed
             * time since last dismissed. A message is considered for display only
             * if the number of seconds since it was last dismissed
             * is greater or equal to the specified `displayInterval`.
             */
            if (storedMessage.lastDismissDate) {
                const lastDismissDate = new Date(storedMessage.lastDismissDate);
                const today = new Date();
                const diffInMs = today.getTime() - lastDismissDate.getTime();
                const diffInSeconds = diffInMs / 1000; // Covert milliseconds to seconds

                if (diffInSeconds < storedMessage.displayFrequency) {
                    return;
                }
            }

            displayMessage(storedMessage);
        }

        function findUserMessage(messages: ServerMessage[]): ServerMessage {
            /**
             * If message is not dismissible, it should be displayed
             * for every user.
             */
            return messages.find(
                (message) => !message.dismissible || message.userId === userId
            );
        }

        function displayMessage(message: ServerMessage): void {
            const bodyElement = angular.element(document.body);
            messageHtmlElement = angular.element(
                '<im-server-message message="message"></im-server-message>'
            );
            scope = $rootScope.$new();
            scope.message = message;

            bodyElement.prepend(messageHtmlElement);

            $compile(messageHtmlElement)(scope);
        }

        function hideMessage(): void {
            if (scope) {
                scope.$destroy();
            }

            if (messageHtmlElement) {
                messageHtmlElement.remove();
            }
        }
    }
})();



// WEBPACK FOOTER //
// ./node_modules/angularjs-template-loader??ref--0-1!./src/app/components/server-message/server-message.service.ts